import React, { useContext } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import { injectIntl } from 'react-intl';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableBody from '@material-ui/core/TableBody';
import MUITablePagination from '@material-ui/core/TablePagination';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';

import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import ContextProvider from '../../../context';

import messages from './tableMessages';

import s from './Table.scss';

const Row = ({
  className,
  horizontalOnMobile,
  includeTablet,
  children,
  ...rest
}) => (
  <TableRow
    className={classNames(
      {
        [s.tableRow]: !horizontalOnMobile,
        [s.tableRowHorizontal]: horizontalOnMobile,
        [s.includeTablet]: includeTablet,
      },
      className,
    )}
    {...rest}
  >
    {children}
  </TableRow>
);

Row.propTypes = {
  horizontalOnMobile: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  includeTablet: PropTypes.bool,
};

Row.defaultProps = {
  horizontalOnMobile: false,
  children: null,
  className: '',
  includeTablet: false,
};

const Cell = ({ className, children, includeTablet, title, ...rest }) => (
  <TableCell
    data-title={title}
    className={classNames(
      s.tableCell,
      { [s.includeTablet]: includeTablet },
      { [s.cellBefore]: !!title },
      s.generalStylesCell,
      className,
    )}
    {...rest}
  >
    {children}
  </TableCell>
);

Cell.propTypes = {
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  children: PropTypes.node,
  className: PropTypes.string,
  includeTablet: PropTypes.bool,
};

Cell.defaultProps = {
  children: null,
  title: '',
  className: '',
  includeTablet: false,
};

const TableWrap = ({
  className,
  children,
  includeTablet,
  loading,
  ...rest
}) => (
  <div className={s.lineOverflow}>
    <Table
      className={classNames(s.table, className, {
        [s.includeTablet]: includeTablet,
        [s.loading]: loading,
      })}
      {...rest}
    >
      {children}
    </Table>
  </div>
);

TableWrap.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  includeTablet: PropTypes.bool,
  loading: PropTypes.bool,
};

TableWrap.defaultProps = {
  children: null,
  className: '',
  includeTablet: true,
  loading: false,
};

const BodyWrap = ({
  className,
  intl: { formatMessage },
  isEmpty,
  children,
  emptyMessage,
  includeTablet,
  ...rest
}) => {
  const context = useContext(ContextProvider);

  const { organizationSlug } = context;

  return (
    <TableBody className={classNames(className)} {...rest}>
      {isEmpty ? (
        <TableRow>
          <TableCell
            className={classNames(s.tableBodyEmptyRow, s.includeTablet, {
              [s.whitelabeled]: !!organizationSlug,
            })}
            colSpan={24}
          >
            <div
              className={classNames(s.emptyTableImg, {
                [s.whitelabeled]: !!organizationSlug,
              })}
            />
            {formatMessage(emptyMessage)}
          </TableCell>
        </TableRow>
      ) : (
        children
      )}
    </TableBody>
  );
};

BodyWrap.propTypes = {
  children: PropTypes.node,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  className: PropTypes.string,
  emptyMessage: PropTypes.shape({
    id: PropTypes.string.isRequired,
    defaultMessage: PropTypes.string.isRequired,
  }),
  isEmpty: PropTypes.bool.isRequired,
  includeTablet: PropTypes.bool,
};

BodyWrap.defaultProps = {
  children: null,
  className: '',
  emptyMessage: messages.emptyTableMessage,
  includeTablet: false,
};

const EmptyRow = ({
  width,
  count,
  colSpan,
  rowHeight,
  className,
  breackpointWidth,
}) =>
  isWidthUp(breackpointWidth, width) && count > 0 ? (
    <TableRow
      style={{ height: rowHeight * (count === 10 ? count - 1 : count) }}
      className={className}
    >
      <ResponsiveTableCell colSpan={colSpan} />
    </TableRow>
  ) : null;

EmptyRow.propTypes = {
  count: PropTypes.number.isRequired,
  width: PropTypes.string.isRequired,
  colSpan: PropTypes.number,
  rowHeight: PropTypes.number,
  className: PropTypes.string,
  breackpointWidth: PropTypes.string,
};

EmptyRow.defaultProps = {
  colSpan: 16,
  rowHeight: 49,
  className: '',
  breackpointWidth: 'sm',
};

const ChangeOfInPagination = ({ from, to, count }) =>
  `${from} - ${to} / ${count}`;

const Pagination = ({
  classes,
  intl: { formatMessage },
  withBorder,
  nextIconButtonProps,
  backIconButtonProps,
  ...rest
}) => (
  <MUITablePagination
    labelDisplayedRows={ChangeOfInPagination}
    classes={{
      toolbar: s.tablePaginationToolbar,
      actions: s.tablePaginationActions,
      ...classes,
      root: classNames(
        { [s.tablePaginationRoot]: !withBorder },
        _.get(classes, 'root'),
      ),
    }}
    nextIconButtonProps={{
      'aria-label': formatMessage(messages.nextIconAriaLabel),
      title: formatMessage(messages.nextIconAriaLabel),
      ...nextIconButtonProps,
    }}
    backIconButtonProps={{
      'aria-label': formatMessage(messages.backIconAriaLabel),
      title: formatMessage(messages.backIconAriaLabel),
      ...backIconButtonProps,
    }}
    {...rest}
  />
);

Pagination.propTypes = {
  ...MUITablePagination.propTypes,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

export const GroupChipWithoutStyles = ({ name }) => (
  <Tooltip title={name}>
    <Chip label={name} className={s.chip} />
  </Tooltip>
);
GroupChipWithoutStyles.propTypes = {
  name: PropTypes.string.isRequired,
};

export const ResponsiveTable = withStyles(s)(TableWrap);

export const ResponsiveTableRow = withStyles(s)(Row);

export const ResponsiveTableCell = withStyles(s)(Cell);

export const ResponsiveTableBody = injectIntl(withStyles(s)(BodyWrap));

export const EmptyRows = withWidth()(EmptyRow);

export const TablePagination = injectIntl(withStyles(s)(Pagination));

export const GroupChip = withStyles(s)(GroupChipWithoutStyles);
